import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Drawer } from "@mui/material";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { RadixSelectControl, RadixSelectOption } from "../../../components/ui/radix-select";
import { Icon } from "../../../components/Shared/Icon";
import { getProtocolList } from "../../Administrations/helpers";
import { AuthenticationOptions, PrivacyOptions, SnmpVersions } from "../../../globals/constants";
import { isIPv4 } from "../../../utils/validations";
import { isDasModel } from "../helpers";
import { setErrorMsg, setOpenToast } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { IFullDeviceDetails } from "../../../store/api/deviceConfig/types";
import { setSelectedDevice } from "../../../store/api/devices/reducers";
import { useAddDeviceSiteMutation, useUpdateDasUiPortMutation } from "../../../store/api/admin/adminApiSlice";
import { prepareOfflineSnmpPayload } from "../../../components/Administrations/AccessControls/Authentication/helpers";
import { errorMessage } from "../../../utils/helpers";

interface ISnmpDrawerProps {
    open: boolean;
    onClose: () => void;
}

interface ISnmpFormValues {
    ipAddress: string;
    snmpPort: string;
    timeout: string;
    snmpVersion: string;
    readCommunity: string;
    writeCommunity: string;
    username: string;
    contextName: string;
    authProtocol: string;
    authPassword: string;
    privacyProtocol: string;
    privacyPassword: string;
    uiPort: string;
}

const SnmpDrawer: React.FC<ISnmpDrawerProps> = ({ open, onClose }) => {
    const dispatch = useStoreDispatch();
    const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
    const errorMsg = useSelector(errorMsgSelector) as any;
    const [addOfflineSite] = useAddDeviceSiteMutation();
    const [updateDasUiPort] = useUpdateDasUiPortMutation();

    const [formValues, setFormValues] = useState<ISnmpFormValues>({
        ipAddress: selectedDevice?.ipAddress || "",
        snmpPort: selectedDevice?.port?.toString() || "",
        timeout: selectedDevice?.timeout?.toString() || "",
        snmpVersion: selectedDevice?.snmpVersion?.toString() || "",
        readCommunity: selectedDevice?.readCommunity || "",
        writeCommunity: selectedDevice?.writeCommunity || "",
        username: selectedDevice?.username || "",
        contextName: selectedDevice?.contextName || "",
        authProtocol: selectedDevice?.authProtocol?.toString() || "",
        authPassword: selectedDevice?.authPassword || "",
        privacyProtocol: selectedDevice?.privacyProtocol?.toString() || "",
        privacyPassword: selectedDevice?.privacyPassword || "",
        uiPort: selectedDevice?.urlAddress || "",
    });

    useEffect(() => {
        if (!open) return;
        setFormValues({
            ipAddress: selectedDevice?.ipAddress || "",
            snmpPort: selectedDevice?.port?.toString() || "",
            timeout: selectedDevice?.timeout?.toString() || "",
            snmpVersion: selectedDevice?.snmpVersion?.toString() || "",
            readCommunity: selectedDevice?.readCommunity || "",
            writeCommunity: selectedDevice?.writeCommunity || "",
            username: selectedDevice?.username || "",
            contextName: selectedDevice?.contextName || "",
            authProtocol: selectedDevice?.authProtocol?.toString() || "",
            authPassword: selectedDevice?.authPassword || "",
            privacyProtocol: selectedDevice?.privacyProtocol?.toString() || "",
            privacyPassword: selectedDevice?.privacyPassword || "",
            uiPort: selectedDevice?.urlAddress || "",
        });
    }, [selectedDevice, open]);

    const [showAuthPassword, setShowAuthPassword] = React.useState(false);
    const [showPrivacyPassword, setShowPrivacyPassword] = React.useState(false);

    const snmpVersionOptions = getProtocolList(SnmpVersions) || [];
    const isFormSnmpVersionV2c = formValues.snmpVersion === "1";
    const isDas = isDasModel(selectedDevice?.modelName || "");

    const clearFieldErrors = () => {
        const updated = { ...errorMsg };
        [
            "ipAddress",
            "snmpPort",
            "timeout",
            "snmpVersion",
            "readCommunity",
            "writeCommunity",
            "username",
            "contextName",
            "authProtocol",
            "authPassword",
            "privacyProtocol",
            "privacyPassword",
            "uiPort",
        ].forEach((field) => {
            if (field in updated) {
                updated[field] = "";
            }
        });
        dispatch(setErrorMsg(updated));
    };

    const validateField = (name: string, value: string): string => {
        if (name === "ipAddress") {
            if (!isIPv4(value)) return "Invalid IPv4 Address.";
            return "";
        }

        if (name === "snmpPort") {
            if (!value) return "SNMP Port is required.";
            const num = Number(value);
            if (isNaN(num) || num < 1 || num > 65535) return "SNMP Port must be between 1 and 65535.";
            return "";
        }

        if (name === "timeout") {
            if (!value) return "Timeout is required.";
            const num = Number(value);
            if (isNaN(num) || num < 1 || num > 300) return "Timeout must be between 1 and 300.";
            return "";
        }

        if (name === "timeout") {
            const num = Number(value);
            if (isNaN(num) || num < 1 || num > 300) return "Timeout must be between 1 and 300.";
            return "";
        }

        if (name === "username") {
            if (!isFormSnmpVersionV2c && !value) return "Username is required.";
            return "";
        }

        if (name === "contextName") {
            return "";
        }

        if (name === "readCommunity") {
            if (isFormSnmpVersionV2c && !value) return "Read Community is required.";
            return "";
        }

        if (name === "writeCommunity") {
            if (isFormSnmpVersionV2c && !value) return "Write Community is required.";
            return "";
        }

        if (name === "authPassword") {
            const authRequired = formValues.authProtocol && formValues.authProtocol !== 'NO_AUTH';
            if (!isFormSnmpVersionV2c && authRequired && !value) return "Auth Password is required.";
            return "";
        }

        if (name === "privacyPassword") {
            const privRequired = formValues.privacyProtocol && formValues.privacyProtocol !== 'NO_PRIV';
            if (!isFormSnmpVersionV2c && privRequired && !value) return "Privacy Password is required.";
            return "";
        }

        if (name === "uiPort") {
            if (!value) return "";
            const num = Number(value);
            if (isNaN(num) || !Number.isInteger(num) || num < 1 || num > 65535) return "Device UI Port must be between 1 and 65535.";
            return "";
        }
        return "";
    };

    const onBlurHandler = (event: any) => {
        const { name, value } = event.target;
        const errorText = validateField(name, value);
        dispatch(setErrorMsg({ ...errorMsg, [name]: errorText }));
    };

    const handleOnChange = (event: any) => {
        event.preventDefault();
        const { name, value } = event.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
        dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const validateForm = (): boolean => {
        const fields: Array<keyof ISnmpFormValues> = [
            "ipAddress",
            "snmpPort",
            "timeout",
            "readCommunity",
            "writeCommunity",
            "username",
            "contextName",
            "authProtocol",
            "authPassword",
            "privacyProtocol",
            "privacyPassword"
        ];

        if (isDas) {
            fields.push("uiPort");
        }

        let hasError = false;
        const updated = { ...errorMsg };

        fields.forEach((field) => {
            const errorText = validateField(field, formValues[field]);
            updated[field] = errorText;
            if (errorText) hasError = true;
        });

        dispatch(setErrorMsg(updated));
        return !hasError;
    };

    const handleClose = () => {
        clearFieldErrors();
        onClose();
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        const updatedDevice: IFullDeviceDetails = {
            ...selectedDevice,
            ipAddress: formValues.ipAddress,
            port: Number(formValues.snmpPort),
            timeout: Number(formValues.timeout),
            snmpVersion: Number(formValues.snmpVersion),
            readCommunity: formValues.readCommunity,
            writeCommunity: formValues.writeCommunity,
            username: formValues.username,
            contextName: formValues.contextName,
            authProtocol: formValues.authProtocol,
            authPassword: formValues.authPassword,
            privacyProtocol: formValues.privacyProtocol,
            privacyPassword: formValues.privacyPassword,
            urlAddress: formValues.uiPort?.trim() || "",
        };

        try {
            // Compare against the current device so a port-only change avoids the
            // offline re-add path (which would flip the device to OFFLINE).
            const snmpFields: (keyof ISnmpFormValues)[] = [
                "ipAddress", "snmpPort", "timeout", "snmpVersion", "readCommunity",
                "writeCommunity", "username", "contextName", "authProtocol",
                "authPassword", "privacyProtocol", "privacyPassword",
            ];
            const original: Record<string, string> = {
                ipAddress: selectedDevice?.ipAddress || "",
                snmpPort: selectedDevice?.port?.toString() || "",
                timeout: selectedDevice?.timeout?.toString() || "",
                snmpVersion: selectedDevice?.snmpVersion?.toString() || "",
                readCommunity: selectedDevice?.readCommunity || "",
                writeCommunity: selectedDevice?.writeCommunity || "",
                username: selectedDevice?.username || "",
                contextName: selectedDevice?.contextName || "",
                authProtocol: selectedDevice?.authProtocol?.toString() || "",
                authPassword: selectedDevice?.authPassword || "",
                privacyProtocol: selectedDevice?.privacyProtocol?.toString() || "",
                privacyPassword: selectedDevice?.privacyPassword || "",
            };
            const snmpChanged = snmpFields.some((k) => (formValues[k] || "") !== original[k]);
            const newPort = formValues.uiPort?.trim() || "";
            const portChanged = isDas && newPort !== (selectedDevice?.urlAddress || "");

            if (snmpChanged) {
                const payload = prepareOfflineSnmpPayload(updatedDevice);
                const response: any = await addOfflineSite(payload);
                if (response?.error || response?.data?.status === 401) throw response?.error;
                if (!(response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK")) return;
            } else if (portChanged) {
                const response: any = await updateDasUiPort({ objectKey: selectedDevice.objectKey!, urlAddress: newPort });
                if (response?.error) throw response?.error;
            }

            dispatch(
                setOpenToast({
                    openToast: true,
                    toastType: "success",
                    message: snmpChanged
                        ? "Connection details updated successfully!"
                        : "Device UI port updated successfully!",
                })
            );
            dispatch(setSelectedDevice(updatedDevice));
            clearFieldErrors();
            onClose();
        } catch (error: any) {
            dispatch(
                setOpenToast({
                    openToast: true,
                    toastType: "error",
                    message: error?.data ? JSON.stringify(error?.data) : errorMessage(error),
                })
            );
        }
    };

    const errorPresent = [
        "ipAddress",
        "snmpPort",
        "timeout",
        "snmpVersion",
        "readCommunity",
        "writeCommunity",
        "username",
        "contextName",
        "authProtocol",
        "authPassword",
        "privacyProtocol",
        "privacyPassword",
        "uiPort",
    ].some((field) => !!errorMsg?.[field]);

    return (
        <Drawer
            PaperProps={{
                sx: {
                    width: "560px",
                    maxWidth: "90vw",
                    height: "100%",
                    top: 0,
                    backgroundColor: "var(--color-background)",
                    borderLeft: "1px solid var(--color-border)",
                    borderRadius: "10px 0 0 10px",
                    boxShadow: "-4px 0 24px 0 rgba(0,0,0,0.08)",
                    overflow: "hidden",
                },
            }}
            ModalProps={{ disableEnforceFocus: true }}
            anchor="right"
            open={open}
            onClose={handleClose}
        >
            <div className="flex flex-col h-full min-h-0 bg-background">
                {/* Header */}
                <div className="relative shrink-0 px-6 pt-4 pb-4 border-b border-border">
                    <button
                        type="button"
                        className="absolute right-3 top-3 h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-accent text-foreground border-0 p-0 focus-visible:outline-none"
                        aria-label="Close"
                        onClick={handleClose}
                    >
                        <Icon name="close" size={20} />
                    </button>
                    <h2 className="text-lg font-semibold text-foreground pr-8">Edit SNMP Configuration</h2>
                </div>
                {/* Scrollable body */}
                <div className="flex-1 min-h-0 overflow-auto px-6 py-6">
                    <div className="flex flex-col gap-5">

                        {/* IP Address */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                IP Address <span className="text-destructive">*</span>
                            </label>
                            <Input
                                name="ipAddress"
                                value={formValues.ipAddress}
                                disabled={true}
                                onBlur={onBlurHandler}
                                onChange={handleOnChange}
                            />
                            {errorMsg?.ipAddress && <p className="text-xs text-destructive">{errorMsg.ipAddress}</p>}
                        </div>

                        {/* SNMP Port */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                SNMP Port <span className="text-destructive">*</span>
                            </label>
                            <Input
                                name="snmpPort"
                                type="number"
                                value={formValues.snmpPort}
                                onBlur={onBlurHandler}
                                onChange={handleOnChange}
                            />
                            {errorMsg?.snmpPort && <p className="text-xs text-destructive">{errorMsg.snmpPort}</p>}
                        </div>

                        {/* Device UI Port (DAS only) */}
                        {isDas && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground">
                                    Device UI Port
                                </label>
                                <Input
                                    name="uiPort"
                                    type="number"
                                    placeholder="Default 443"
                                    value={formValues.uiPort}
                                    onBlur={onBlurHandler}
                                    onChange={handleOnChange}
                                />
                                {errorMsg?.uiPort && <p className="text-xs text-destructive">{errorMsg.uiPort}</p>}
                            </div>
                        )}

                        {/* Timeout */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                Timeout <span className="text-destructive">*</span>
                            </label>
                            <Input
                                name="timeout"
                                type="number"
                                value={formValues.timeout}
                                onBlur={onBlurHandler}
                                onChange={handleOnChange}
                            />
                            {errorMsg?.timeout && <p className="text-xs text-destructive">{errorMsg.timeout}</p>}
                        </div>

                        {/* SNMP Version */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">
                                SNMP Version <span className="text-destructive">*</span>
                            </label>
                            <RadixSelectControl
                                options={snmpVersionOptions.map((o): RadixSelectOption => ({ value: o.value, label: o.text }))}
                                value={formValues.snmpVersion}
                                onValueChange={(v) => {
                                    setFormValues((prev) => ({ ...prev, snmpVersion: v }));
                                    dispatch(setErrorMsg({ ...errorMsg, snmpVersion: "" }));
                                }}
                                placeholder="Select SNMP Version..."
                            />
                            {errorMsg?.snmpVersion && <p className="text-xs text-destructive">{errorMsg.snmpVersion}</p>}
                        </div>

                        {isFormSnmpVersionV2c ? (
                            <>
                                {/* Read Community */}
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground">
                                        Read Community <span className="text-destructive">*</span>
                                    </label>
                                    <Input
                                        name="readCommunity"
                                        value={formValues.readCommunity}
                                        onBlur={onBlurHandler}
                                        onChange={handleOnChange}
                                    />
                                    {errorMsg?.readCommunity && <p className="text-xs text-destructive">{errorMsg.readCommunity}</p>}
                                </div>

                                {/* Write Community */}
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground">
                                        Write Community <span className="text-destructive">*</span>
                                    </label>
                                    <Input
                                        name="writeCommunity"
                                        value={formValues.writeCommunity}
                                        onBlur={onBlurHandler}
                                        onChange={handleOnChange}
                                    />
                                    {errorMsg?.writeCommunity && <p className="text-xs text-destructive">{errorMsg.writeCommunity}</p>}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Username */}
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground">
                                        Username <span className="text-destructive">*</span>
                                    </label>
                                    <Input
                                        name="username"
                                        value={formValues.username}
                                        onBlur={onBlurHandler}
                                        onChange={handleOnChange}
                                    />
                                    {errorMsg?.username && <p className="text-xs text-destructive">{errorMsg.username}</p>}
                                </div>

                                {/* Context Name */}
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground">
                                        Context Name
                                    </label>
                                    <Input
                                        name="contextName"
                                        value={formValues.contextName}
                                        onBlur={onBlurHandler}
                                        onChange={handleOnChange}
                                    />
                                    {errorMsg?.contextName && <p className="text-xs text-destructive">{errorMsg.contextName}</p>}
                                </div>

                                {/* Authentication Protocol */}
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground">
                                        Authentication Protocol <span className="text-destructive">*</span>
                                    </label>
                                    <RadixSelectControl
                                        options={(getProtocolList(AuthenticationOptions) || []).map((o): RadixSelectOption => ({ value: o.value, label: o.text }))}
                                        value={formValues.authProtocol?.toString() ?? ""}
                                        onValueChange={(v) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                authProtocol: v,
                                                ...(v === 'NO_AUTH' ? { authPassword: '' } : {}),
                                            }));
                                            dispatch(setErrorMsg({ ...errorMsg, authProtocol: "", authPassword: "" }));
                                        }}
                                        placeholder="Select Protocol..."
                                    />
                                    {errorMsg?.authProtocol && <p className="text-xs text-destructive">{errorMsg.authProtocol}</p>}
                                </div>

                                {/* Authentication Password */}
                                {formValues.authProtocol !== 'NO_AUTH' && (
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground">
                                        Authentication Password {formValues.authProtocol && <span className="text-destructive">*</span>}
                                    </label>
                                    <div className="relative">
                                        <Input
                                            name="authPassword"
                                            type={showAuthPassword ? "text" : "password"}
                                            value={formValues.authPassword}
                                            onBlur={onBlurHandler}
                                            onChange={handleOnChange}
                                            className="pr-10"
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground bg-transparent border-0 p-0"
                                            onClick={() => setShowAuthPassword((p) => !p)}
                                            tabIndex={-1}
                                        >
                                            <Icon name={showAuthPassword ? "visibility_off" : "visibility"} size={16} />
                                        </button>
                                    </div>
                                    {errorMsg?.authPassword && <p className="text-xs text-destructive">{errorMsg.authPassword}</p>}
                                </div>
                                )}

                                {/* Privacy Protocol */}
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground">
                                        Privacy Protocol <span className="text-destructive">*</span>
                                    </label>
                                    <RadixSelectControl
                                        options={(getProtocolList(PrivacyOptions) || []).map((o): RadixSelectOption => ({ value: o.value, label: o.text }))}
                                        value={formValues.privacyProtocol}
                                        onValueChange={(v) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                privacyProtocol: v,
                                                ...(v === 'NO_PRIV' ? { privacyPassword: '' } : {}),
                                            }));
                                            dispatch(setErrorMsg({ ...errorMsg, privacyProtocol: "", privacyPassword: "" }));
                                        }}
                                        placeholder="Select Protocol..."
                                    />
                                    {errorMsg?.privacyProtocol && <p className="text-xs text-destructive">{errorMsg.privacyProtocol}</p>}
                                </div>

                                {/* Privacy Password */}
                                {formValues.privacyProtocol !== 'NO_PRIV' && (
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground">
                                        Privacy Password {formValues.privacyProtocol && <span className="text-destructive">*</span>}
                                    </label>
                                    <div className="relative">
                                        <Input
                                            name="privacyPassword"
                                            type={showPrivacyPassword ? "text" : "password"}
                                            value={formValues.privacyPassword}
                                            onBlur={onBlurHandler}
                                            onChange={handleOnChange}
                                            className="pr-10"
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground bg-transparent border-0 p-0"
                                            onClick={() => setShowPrivacyPassword((p) => !p)}
                                            tabIndex={-1}
                                        >
                                            <Icon name={showPrivacyPassword ? "visibility_off" : "visibility"} size={16} />
                                        </button>
                                    </div>
                                    {errorMsg?.privacyPassword && <p className="text-xs text-destructive">{errorMsg.privacyPassword}</p>}
                                </div>
                                )}
                            </>
                        )}

                    </div>
                </div>
                {/* Footer */}
                <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t border-border">
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={errorPresent}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </Drawer>
    );
};

export default SnmpDrawer;

